import React, { useState, useEffect, useRef, useMemo } from 'react';
import uuid from 'uuid/v4';
import { cloneDeep } from 'lodash';
import CellContent from './CellContent';
import { setHeightOfRows } from '../../../util/CrosstabUtils';

import InfoBox, { InfoBoxSpacer } from '../EnhancedGridFeature/InfoBox';
import GridItemName from '../EnhancedGridFeature/GridItemName';
import EditGridItem from '../EnhancedGridFeature/EditGridItem';
import EditExpression from '../EnhancedGridFeature/EditExpression';
import blueEllipsisIcon from '../../../images/blue-menu-icon.svg';
import GlobalDelete from '../EnhancedGridFeature/GlobalDelete';
import Label from '../EnhancedGridFeature/Label';
import CrosstabColumnHeader from './CrosstabColumnHeader';

import AutocodeLabelsGenerator, {
  ACStandardLabels,
} from '../../autocode/AutocodeLabelsGenerator';

const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);

function CrosstabViewRowHeaders({
  cellElementList,
  getGridRows,
  toggleCheckVal,
  updateGridData,
  handleCrosstabEdit,
  restoreGridTitle,
  singleDeleteOperation,
  updateCrosstabGridTitleHeader,
  crosstabFootNotes,
  crosstabWeightTypes,
  updatedTitleIndex,
  editExpression,
  updateGridTitle,
  updatedTitleGridType,
  resetSelectedIndex,
  partsToShow,
}) {
  const metricsList = cellElementList.map(item => {
    return item.isVisible;
  });
  const metricsCount = cellElementList.filter(item => item.isVisible);
  const newRows = cloneDeep(getGridRows);

  const getHeaderLabel = (header, fromMenu = false) => {
    let title = '';
    if (header.isRowLabelUpdated && !fromMenu) {
      title = header.rowHeader;
    } else if (header.autocodenode) {
      title = toLabelString(header.autocodenode, partsToShow, fromMenu);
    } else {
      title = `${header.tooltipVal ? header.tooltipVal : header.rowHeader}`;
    }
    if (title.includes('error')) {
      title = header.rowHeader;
    }
    return title;
  };

  const buildInfoBox = (row, showingFullNames, rowIndex) => {
    const ibp = [];
    if (!showingFullNames || row.isRowLabelUpdated) {
      const txt = getHeaderLabel(row, true);
      ibp.push(
        <GridItemName
          header="Original Name"
          body={txt}
          ShowRestoreButton={row.isRowLabelUpdated}
          restoreGridTitle={restoreGridTitle}
          index={rowIndex}
          gridType="RowsDataGrid"
        />
      );
      ibp.push(
        <InfoBoxSpacer
          Height="1em"
          boxClassName="toolPalette-spacer-row ibox-spacer-hline"
        />
      );
    }
    ibp.push(
      <EditGridItem
        updateGridTitle={updateGridTitle}
        index={rowIndex}
        gridType="RowsDataGrid"
      />
    );
    ibp.push(
      <EditExpression
        handleEditExpression={handleCrosstabEdit}
        index={rowIndex}
        gridType="RowsDataGrid"
      />
    );
    ibp.push(
      <InfoBoxSpacer
        Height="0.1em"
        boxClassName="toolPalette-spacer-row ibox-spacer-hline"
      />
    );
    ibp.push(
      <GlobalDelete
        label="Clear Row"
        deleteType="single"
        gridType="RowsDataGrid"
        type="row"
        infoBox
        index={rowIndex}
        singleDeleteOperation={singleDeleteOperation}
      />
    );
    return ibp;
  };

  return useMemo(
    () =>
      newRows.map((row, rIndex) => (
        <div
          className="crosstabViewerGrid-row"
          key={uuid()}
          style={{
            height: setHeightOfRows(
              partsToShow,
              row,
              rIndex,
              toggleCheckVal,
              'Crosstab-View',
              metricsList
            ),
          }}
        >
          <div
            data-tooltip
            aria-haspopup="true"
            title={`${getHeaderLabel(row)} ${
              row.isRowLabelUpdated ? String.fromCharCode(185) : ''
            }`}
            data-line-clamp={metricsCount}
            className={
              toggleCheckVal.row
                ? 'padding-row-title left-header-content left-header-content-ellipsis'
                : 'left-header-content left-header-content-ellipsis'
            }
          >
            <div
              style={{
                width: '100%',
                WebkitLineClamp: metricsCount.length,
                height: setHeightOfRows(
                  partsToShow,
                  row,
                  rIndex,
                  toggleCheckVal,
                  'Crosstab-View',
                  metricsList
                ),
              }}
            >
              <Label
                editEnabled={
                  updatedTitleIndex === rIndex &&
                  updatedTitleGridType === 'RowsDataGrid'
                }
                label={getHeaderLabel(row)}
                toggleLabel={toggleCheckVal.row}
                gridType="RowsDataGrid"
                index={rIndex}
                minRows={3}
                maxRows={8}
                cols={50}
                minHeight="35px"
                maxHeight="120px"
                style={{
                  left: '0px',
                  backgroundColor: '#EDF0F3',
                  color: 'black',
                }}
                updateCrosstabGridTitleHeader={updateCrosstabGridTitleHeader}
                updateGridData={updateGridData}
                resetSelectedIndex={resetSelectedIndex}
                isLabelUpdated={row.isRowLabelUpdated}
                showFootNoteIcon={row.footnotes ? row.footnotes : []}
                crosstabFootNotes={crosstabFootNotes}
              />
            </div>
            {rIndex !== 0 && (
              <div
                className="c-table--row-infobox-ellipsis"
                style={{ display: 'contents' }}
              >
                <InfoBox
                  Panels={buildInfoBox(row, toggleCheckVal.row, rIndex)}
                  Positions={['right']}
                  InfoBoxButtonInner={
                    <img
                      className="ibox-ellipsis ibox-ellipsis-top-padding"
                      src={blueEllipsisIcon}
                      alt="..."
                    />
                  }
                  Align="start"
                  containerClassName={
                    getHeaderLabel(row).length < 390
                      ? 'ibox-popover-container'
                      : !toggleCheckVal.row || row.isRowLabelUpdated
                      ? 'ibox-popover-container-longText'
                      : 'ibox-popover-container'
                  }
                />
              </div>
            )}
          </div>
          <CrosstabColumnHeader
            metricsList={metricsList}
            metricsCount={metricsCount.length}
            crosstabWeightTypes={crosstabWeightTypes}
            rowHeight={setHeightOfRows(
              partsToShow,
              row,
              rIndex,
              toggleCheckVal,
              'Crosstab-View',
              metricsList
            )}
          />
        </div>
      )),
    [
      newRows,
      cellElementList,
      getGridRows,
      toggleCheckVal.row,
      crosstabFootNotes,
      editExpression,
      crosstabWeightTypes,
      updatedTitleIndex,
      updatedTitleGridType,
      metricsList,
      metricsCount,
    ]
  );
}

export default React.memo(CrosstabViewRowHeaders);
